import type { DoorSettings } from "building-types/settings/doorSettings";
import type { ActiveProfileSource } from "building-types/settings/profileCatalogSettings";
import type { WallMassSystem, WallSystem } from "building-types/walls/wallSytemTypes";
import type { Table } from "common/data/table";
import { assertIsNotUndefined } from "common/utils/assertOfType";
import { planarGraphAdjacentHalfEdge } from "geom/planarGraph";
import type { PolygonPlanarGraph } from "geom/polygonPlanarGraph";
import type { Ray2 } from "geom/ray2Types";
import {
  DefaultDoorMarchTraversalOptions,
  traverseWallIntervalLoopCCW,
  traverseWallIntervalLoopCW,
} from "./traverseWallIntervalLoop";
import { makeWallIntervalLoop } from "./wallIntervalLoop";
import type { RoomEdgeBasedEntity, TraverseOptions, WallInterval, WallIntervalLoop } from "./wallTraverseTypes";
import { TraverseDir, TraverseSide } from "./wallTraverseTypes";

// Class for traversing walls in both directions from a given entity
export class WallTraverseSystem {
  // half edges that have been traversed
  private halfEdgesPerLevel: { [levelId: string]: HalfEdgeFlag } = {};
  // cached table of wall interval loops
  private traversesPerLevel: { [levelId: string]: WallIntervalLoop[] } = {};
  // lookup table for finding entities in cached wall interval loops
  private lookups: EntityExtentLookups = {
    frontLeftExtentLookup: {},
    frontRightExtentLookup: {},
    backLeftExtentLookup: {},
    backRightExtentLookup: {},
  };

  constructor(
    private readonly planarGraphs: Table<PolygonPlanarGraph>,
    private readonly wallSystems: Table<WallSystem>,
    private readonly wallMassSystems: Table<WallMassSystem>,
    private readonly doorSettings: DoorSettings,
    private readonly profileSource: ActiveProfileSource,
  ) {}

  traverseBothDirectionsFromEntity(
    entity: RoomEdgeBasedEntity,
    side: TraverseSide,
    options: TraverseOptions = DefaultDoorMarchTraversalOptions,
  ): { leftResult: Ray2 | null; rightResult: Ray2 | null } {
    assertIsNotUndefined(entity.frontRoom);

    const levelId = entity.frontRoom.level.id;
    const levelHalfEdges = this.halfEdgesPerLevel[levelId] || {};
    const wallSystem = this.wallSystems[levelId];
    const wallMassSystem = this.wallMassSystems[levelId];
    const polygonPlanarGraph = this.planarGraphs[levelId];
    const { planarGraph } = polygonPlanarGraph;
    const halfEdgeIndex =
      side === TraverseSide.FRONT ? entity.halfEdgeIndex : planarGraphAdjacentHalfEdge(entity.halfEdgeIndex);

    // if half edge has not been encountered in any cached wall interval loop, make a new loop
    if (!levelHalfEdges[halfEdgeIndex]) {
      const {
        loop: traverseLoop,
        collinearJoinedVertices,
        halfEdges: hes,
      } = makeWallIntervalLoop({
        planarGraph,
        wallMassSystem,
        wallSystem,
        initialHalfEdgeIndex: halfEdgeIndex,
        profileSource: this.profileSource,
        doorSettings: this.doorSettings,
      });

      this.cacheWallIntervalLoop(levelId, collinearJoinedVertices, traverseLoop, levelHalfEdges, hes);

      const leftResult =
        side === TraverseSide.FRONT
          ? this.traverse(entity.id, TraverseDirection.FRONT_LEFT, options)
          : this.traverse(entity.id, TraverseDirection.BACK_LEFT, options);
      const rightResult =
        side === TraverseSide.FRONT
          ? this.traverse(entity.id, TraverseDirection.FRONT_RIGHT, options)
          : this.traverse(entity.id, TraverseDirection.BACK_RIGHT, options);

      // Useful for debugging
      //debugWallTraverse(traverseLoop, polyLine2New(collinearJoinedVertices));

      return { leftResult, rightResult };
    }

    // use cached wall interval loop
    const leftResult =
      side === TraverseSide.FRONT
        ? this.traverse(entity.id, TraverseDirection.FRONT_LEFT, options)
        : this.traverse(entity.id, TraverseDirection.BACK_LEFT, options);
    const rightResult =
      side === TraverseSide.FRONT
        ? this.traverse(entity.id, TraverseDirection.FRONT_RIGHT, options)
        : this.traverse(entity.id, TraverseDirection.BACK_RIGHT, options);

    return { leftResult, rightResult };
  }

  traverseOneDirectionFromEntity(
    entity: RoomEdgeBasedEntity,
    side: TraverseSide,
    dir: TraverseDir,
    options: TraverseOptions = DefaultDoorMarchTraversalOptions,
  ): Ray2 | null {
    assertIsNotUndefined(entity.frontRoom);
    const levelId = entity.frontRoom.level.id;
    const levelHalfEdges = this.halfEdgesPerLevel[levelId] || {};
    const wallSystem = this.wallSystems[levelId];
    const wallMassSystem = this.wallMassSystems[levelId];
    const polygonPlanarGraph = this.planarGraphs[levelId];
    const { planarGraph } = polygonPlanarGraph;
    const halfEdgeIndex =
      side === TraverseSide.FRONT ? entity.halfEdgeIndex : planarGraphAdjacentHalfEdge(entity.halfEdgeIndex);

    // test if the half edge has already been traversed
    if (!levelHalfEdges[halfEdgeIndex]) {
      const {
        loop: traverseLoop,
        collinearJoinedVertices,
        halfEdges: hes,
      } = makeWallIntervalLoop({
        planarGraph,
        wallSystem,
        wallMassSystem,
        initialHalfEdgeIndex: halfEdgeIndex,
        profileSource: this.profileSource,
        doorSettings: this.doorSettings,
      });

      // cache information about the traversal and the entities included in the traversal
      this.cacheWallIntervalLoop(levelId, collinearJoinedVertices, traverseLoop, levelHalfEdges, hes);

      let transverseDirection: TraverseDirection;

      if (side === TraverseSide.FRONT) {
        transverseDirection = dir === TraverseDir.LEFT ? TraverseDirection.FRONT_LEFT : TraverseDirection.FRONT_RIGHT;
      } else {
        transverseDirection = dir === TraverseDir.LEFT ? TraverseDirection.BACK_LEFT : TraverseDirection.BACK_RIGHT;
      }

      return this.traverse(entity.id, transverseDirection, options);
    }

    let transverseDirection: TraverseDirection;

    if (side === TraverseSide.FRONT) {
      transverseDirection = dir === TraverseDir.LEFT ? TraverseDirection.FRONT_LEFT : TraverseDirection.FRONT_RIGHT;
    } else {
      transverseDirection = dir === TraverseDir.LEFT ? TraverseDirection.BACK_LEFT : TraverseDirection.BACK_RIGHT;
    }

    return this.traverse(entity.id, transverseDirection, options);
  }

  private cacheWallIntervalLoop(
    levelId: string,
    collinearJoinedVertices: number[],
    traverseLoop: WallInterval[][],
    levelHalfEdges: HalfEdgeFlag,
    hes: { [halfEdgeIndex: number]: boolean },
  ) {
    const levelTraverseLoops = this.traversesPerLevel[levelId] || [];
    levelTraverseLoops.push({ collinearJoinedVertices, intervals: traverseLoop });
    const index = levelTraverseLoops.length - 1;

    // index the entities along the traversal so that they can be looked up later
    cacheEntityIndicesInLoop(traverseLoop, this.lookups, levelId, index);

    this.traversesPerLevel[levelId] = levelTraverseLoops;
    this.halfEdgesPerLevel[levelId] = { ...levelHalfEdges, ...hes };
  }

  private lookupIndex(id: string, direction: TraverseDirection) {
    switch (direction) {
      case TraverseDirection.FRONT_LEFT:
        return this.lookups.frontLeftExtentLookup[id];
      case TraverseDirection.FRONT_RIGHT:
        return this.lookups.frontRightExtentLookup[id];
      case TraverseDirection.BACK_LEFT:
        return this.lookups.backLeftExtentLookup[id];
      case TraverseDirection.BACK_RIGHT:
        return this.lookups.backRightExtentLookup[id];
    }
  }

  private traverse(
    id: string,
    direction: TraverseDirection,
    options: TraverseOptions = DefaultDoorMarchTraversalOptions,
  ) {
    const lookup = this.lookupIndex(id, direction);

    if (!lookup) {
      return null;
    }

    const {
      levelId,
      address: [loopIndex, edgeIndex, intervalIndex],
    } = lookup;

    const traverse = this.traversesPerLevel[levelId][loopIndex];

    switch (direction) {
      case TraverseDirection.FRONT_LEFT:
        return traverseWallIntervalLoopCCW(traverse, edgeIndex, intervalIndex, options);
      case TraverseDirection.FRONT_RIGHT:
        return traverseWallIntervalLoopCW(traverse, edgeIndex, intervalIndex, options);
      case TraverseDirection.BACK_LEFT:
        return traverseWallIntervalLoopCCW(traverse, edgeIndex, intervalIndex, options);
      case TraverseDirection.BACK_RIGHT:
        return traverseWallIntervalLoopCW(traverse, edgeIndex, intervalIndex, options);
    }
  }
}

// store the indices of the entities in the wall interval loop so that they can be looked up later
function cacheEntityIndicesInLoop(
  wallTraverseLoop: WallInterval[][],
  lookups: EntityExtentLookups,
  levelId: string,
  loopIndex: number,
) {
  for (const solidWallIndex of Object.keys(wallTraverseLoop)) {
    const intervals = wallTraverseLoop[solidWallIndex];

    for (const intervalIndex in intervals) {
      const interval = intervals[intervalIndex];

      for (const entityId in interval.right.extentIds) {
        if (interval.frontEntityIds[entityId]) {
          lookups.frontRightExtentLookup[entityId] = { levelId, address: [loopIndex, +solidWallIndex, +intervalIndex] };
        } else {
          lookups.backRightExtentLookup[entityId] = { levelId, address: [loopIndex, +solidWallIndex, +intervalIndex] };
        }
      }

      for (const entityId in interval.left.extentIds) {
        if (interval.frontEntityIds[entityId]) {
          lookups.frontLeftExtentLookup[entityId] = { levelId, address: [loopIndex, +solidWallIndex, +intervalIndex] };
        } else {
          lookups.backLeftExtentLookup[entityId] = { levelId, address: [loopIndex, +solidWallIndex, +intervalIndex] };
        }
      }
    }
  }
}

type IntervalEntityLookup = { [entityId: string]: { levelId: string; address: number[] } };
interface EntityExtentLookups {
  readonly frontRightExtentLookup: IntervalEntityLookup;
  readonly frontLeftExtentLookup: IntervalEntityLookup;
  readonly backRightExtentLookup: IntervalEntityLookup;
  readonly backLeftExtentLookup: IntervalEntityLookup;
}

enum TraverseDirection {
  FRONT_LEFT = "FRONT_LEFT",
  FRONT_RIGHT = "FRONT_RIGHT",
  BACK_LEFT = "BACK_LEFT",
  BACK_RIGHT = "BACK_RIGHT",
}

type HalfEdgeFlag = { [halfEdgeIndex: number]: boolean };
